/**
 * The state shapes (web-plan.md F1) — the web twin of the desktop's
 * `state/` + `runtime/mod.rs` UiState, carrying exactly what F1–F3 screens
 * read. Slices are deliberately flat and JSON-ish: Immer shares unchanged
 * plain objects and arrays across snapshots, and every rule from the porting contract
 * (dedup by seq, unread as a formula, snapshot-not-delta) is written against
 * these shapes in `mutators.ts`.
 */
import type {
  Call,
  ChannelCard,
  ChannelSettings,
  FriendsSnapshot,
  LinkPreview,
  Message,
  ReactionGroup,
  UserStatus,
} from "../gen/dto";

/** The optimistic bubble's sort key: above every real seq, below nothing.
 * (The Rust client uses u64::MAX; seqs here are JSON numbers and stay far
 * under 2^53, so MAX_SAFE_INTEGER is the same statement.) */
export const PENDING_SEQ = Number.MAX_SAFE_INTEGER;

export const WINDOW_SIZE = 300;
export const PAGE_SIZE = 50;
export const TYPING_SEND_PERIOD_MS = 5_500;
export const TYPING_EXPIRY_MS = 8_000;
export const TICK_MS = 2_000;
export const DIRECTORY_TTL_MS = 30_000;
export const AFK_AFTER_MS = 300_000;
export const RING_FOR_MS = 30_000;
export const BOTTOM_SLACK = 48;
export const TOP_TRIGGER = 240;
export const SETTLE_MS = 700;
/** How long the reading position has to hold still before what it shows counts
 * as read (D249): a pause of the eye, not a wheel notch on the way past — and
 * what keeps a flick through a hundred messages down to a handful of marks. */
export const SEEN_SETTLE_MS = 250;
/** How many of the newest readings of the gateway's clock the offset is taken
 * over (D298): enough for one of them to have crossed a quiet network, few
 * enough that a clock set right mid-session is believed within eight messages. */
export const CLOCK_SAMPLES = 8;

export type SendState = "sent" | "preparing" | "sending" | "failed";

export interface Bubble {
  /** Rendering identity survives adoption of the server sequence number. */
  uiKey: string;
  message: Message;
  senderName: string;
  isOwn: boolean;
  /** Computed by regroup(): whether this bubble starts a visual group. */
  showHeader: boolean;
  state: SendState;
  sendProgress: number;
  /** Object URLs shown before the upload finishes (F2). */
  localPreviews: string[];
  /** The header over a reply (D273), from the quote the reply itself carries
   * (`message.reply_to`): who wrote what it answers — resolveNames() — and
   * the start of what that says — fillReplyPreviews(). */
  replyAuthor: string | null;
  replyPreview: string | null;
  /** What it answers was deleted: the header stays, says so, and is nothing
   * to press. A reply with neither words nor this flag carries nothing
   * readable about its target and draws a header without words. */
  replyGone: boolean;
  /** Reactions live BESIDE the message (D147): an edit echo replaces the
   * message whole and the chips must survive it. */
  reactions: ReactionGroup[];
  myReaction: string | null;
}

export type ComposerMode =
  | { t: "new" }
  | { t: "replying"; seq: number }
  | {
      t: "editing";
      seq: number;
    };

export interface FeedState {
  channelId: number; // 0 = empty pane
  channelName: string;
  bubbles: Bubble[];
  maxSeq: number;
  lastReadSeq: number;
  reachedStart: boolean;
  /** The window holds the channel's end: a page said so and nothing arrived
   * past it since (the desktop's `Feed::reached_end`, D271). Then a seq
   * between the newest bubble and `maxSeq` is a deleted message, never a
   * missed one — which is what lets the read mark reach the ceiling (D277). */
  reachedEnd: boolean;
  loadingOlder: boolean;
  /** Between open() and the first page: "no messages" and "not arrived yet"
   * must not draw alike. */
  loading: boolean;
  /** The seq the unread separator sits ABOVE, or null. */
  dividerBefore: number | null;
  mode: ComposerMode;
  /** Every new reply/edit and channel opening invalidates deferred composer cleanup. */
  composerRevision: number;
  error: string | null;
  /** user_id -> last typing frame (ms). Iterated in ascending id order. */
  typing: Record<number, number>;
  lastTypingSentMs: number;
}

export function emptyFeed(): FeedState {
  return {
    channelId: 0,
    channelName: "",
    bubbles: [],
    maxSeq: 0,
    lastReadSeq: 0,
    reachedStart: false,
    reachedEnd: false,
    loadingOlder: false,
    loading: false,
    dividerBefore: null,
    mode: { t: "new" },
    composerRevision: 0,
    error: null,
    typing: {},
    lastTypingSentMs: 0,
  };
}

export interface Room {
  channelId: number;
  ownerId: number;
  kind: string;
  peerId: number | null;
  name: string;
  description: string | null;
  avatarFileId: string | null;
  preview: string;
  /** The seq of the message `preview` quotes, 0 for none — what an edit or a
   * deletion is matched against. Not `maxSeq`: once the newest message is
   * deleted the line quotes an older one (D253). */
  previewSeq: number;
  /** When the quoted message goes (its `expires_at_ms`, the gateway's clock),
   * or null for one that stays: the line is a copy of its words, and has to
   * let go of them at the same deadline the chat does (D298). */
  previewExpiresAtMs: number | null;
  timeText: string;
  unread: number;
  maxSeq: number;
  lastReadSeq: number;
  /** Seqs past the read mark this client saw deleted (D277): the badge's
   * subtraction counts them, and refreshUnread() takes them back off. */
  holes: number[];
  lastMessageAtMs: number;
  callMembers: string[];
  callMemberIds: number[];
  member: boolean;
  admin: boolean;
  settings: ChannelSettings;
  members: number;
  joining: boolean;
}

export function isDmRoom(room: Room): boolean {
  return room.kind === "dm";
}

/**
 * A group (D274): a channel whose door is shut for good. It sits on the
 * Channels tab like any channel; only its settings say it cannot be opened.
 */
export function isGroupRoom(room: Room): boolean {
  return room.kind === "group";
}

export type SidebarTab = "all" | "channels" | "friends";

export interface Person {
  username: string;
  displayName: string | null;
  bio: string | null;
  avatarFileId: string | null;
  /** Unix seconds — the avatar colour seed's second half (D73). */
  createdAt: number;
  bubbleLight: string | null;
  bubbleDark: string | null;
}

export interface DirectoryState {
  people: Record<number, Person>;
  selfId: number;
  selfIdFallback: number;
  lastRefreshMs: number | null;
  refreshWanted: boolean;
}

export interface FriendsState extends FriendsSnapshot {
  /** The first snapshot seeds silently — no toasts for a world that merely
   * already existed (D174). */
  seeded: boolean;
}

export interface PresenceState {
  /** Sparse: absent = offline (and an unknown status draws nothing). */
  map: Record<number, UserStatus>;
  /** The last AFK state we REPORTED — the hysteresis for activity frames. */
  selfAfk: boolean;
  /** Set solely by the presence key's presence in GET /v1/state — the
   * capability probe that gates ever sending an activity frame. */
  enabled: boolean;
}

export interface AccountState {
  displayName: string | null;
  bio: string | null;
  avatarFileId: string | null;
  createdAt: number;
  bubbleLight: string | null;
  bubbleDark: string | null;
  manualStatus: "dnd" | "invisible" | null;
  isAdmin: boolean;
}

export interface Ringing {
  channelId: number;
  starterName: string;
  channelName: string;
  sinceMs: number;
}

export interface Toast {
  id: number;
  text: string;
  /** Clicking the toast opens this person's card (the request toast). */
  userId: number | null;
}

export interface VoicePeer {
  userId: number;
  senderId: number;
  username: string;
  muted: boolean;
  deafened: boolean;
  serverMuted: boolean;
  rttMs: number;
  screenSharing: boolean;
  screenSharePaused: boolean;
  screenShareHasAudio: boolean;
}

/** The one stream we are watching (F5) — null when none. */
export interface ShareView {
  userId: number;
  paused: boolean;
  hasAudio: boolean;
  /** Share audio's own volume, 0..2 (D99). */
  volume: number;
}

/** Our own live call (F4) — null when not in one. The read-only `calls`
 * map keeps describing every room's call; this slice is the one we are
 * inside, with the state only a participant has. */
export interface VoiceCall {
  channelId: number;
  /** "connecting" until media is up AND the MLS group is usable. */
  phase: "connecting" | "up";
  muted: boolean;
  deafened: boolean;
  /** The R16 verification code — the proof E2EE is unbroken, matched by
   * reading it aloud against another participant's screen. */
  code: string | null;
  peers: VoicePeer[];
  /** Who the SFU currently forwards (top-3), as user ids, self included. */
  speaking: number[];
  rttMs: number;
  lossPct: number;
  concealmentPct: number;
  /** Per-person receive volume, 0..2 (D33); absent = 1. */
  volumes: Record<number, number>;
  share: ShareView | null;
}

export interface CardState {
  userId: number | null;
  /** null while the friendship answer flies — the card draws a placeholder,
   * never defaults (a card that briefly offers "Add" to a friend teaches
   * distrust). */
  relation: import("../gen/dto").Friendship | null;
}

/** How far this session got with a page's card (D280), by the URL as the
 * message spells it. `none` is for good when `retryAt` is null — the gateway
 * had nothing to show, or refused the address, or is older than the route —
 * and otherwise until then: over the fetch budget (429), a fetch that outlived
 * its request (503), a network or a server in trouble. A card draws only
 * `ready`. */
export type LinkPreviewEntry =
  | { t: "loading" }
  | { t: "ready"; preview: LinkPreview }
  | { t: "none"; retryAt: number | null };

/** The same for the card of a channel this reader holds no row of (D281),
 * keyed by `channelCardKey`: a 404 — unknown, not visible, a token that is
 * not the channel's live invite — is `none` for good. */
export type ChannelCardEntry =
  | { t: "loading" }
  | { t: "ready"; card: ChannelCard }
  | { t: "none"; retryAt: number | null };

/** One entry per channel *and* token: the same channel behind a live invite
 * and behind a spent one are two different answers. */
export function channelCardKey(channelId: number, token?: string | null): string {
  return token ? `${channelId}:${token}` : `${channelId}`;
}

/** What the card under a link to a channel draws (D279), flat, so a card can
 * subscribe to it shallowly — from the reader's own row when they hold one,
 * and from `GET /v1/channels/{id}/card` (D281) when they do not. */
export interface ChannelLinkView {
  /** The row is the reader's: the channel can be opened from here. */
  held: boolean;
  kind: string;
  name: string;
  description: string | null;
  avatarFileId: string | null;
  /** The circle's second seed: a direct conversation wears its peer's face,
   * as its sidebar row does; every other channel is seeded by name alone. */
  faceCreatedAt: number;
  members: number;
  member: boolean;
  isPrivate: boolean;
  /** A join from the row's own bar or from a card is in flight. */
  joining: boolean;
}

/** The gateway's clock against this machine's (disappearing-messages.md D298).
 * A deadline is written on the gateway's clock and let go of on this one, and
 * the two can disagree by more than a short lifetime. Each reading is the
 * gateway's clock as something left it, less `Date.now()` as it arrived — the
 * `sent_at_ms` of a message handed over the moment it was stored (a live
 * frame, the answer to a send), or `server_time_ms` of `GET /v1/state`, the
 * reading that comes before the first page — so it is the true offset less
 * the time spent on the way: never above it, and the largest of the last few
 * is the one that travelled fastest. */
export interface ClockState {
  /** The newest `CLOCK_SAMPLES` readings, oldest first. */
  samples: number[];
  /** Their maximum — gateway minus local, ms; 0 while there are none. */
  offsetMs: number;
}

export interface UiState {
  connected: boolean;
  busy: boolean;
  /** THE one transient error line. */
  notice: string | null;
  /** Signed in, but the gateway opens nothing until this account proves an
   * e-mail address (D311, `GET /v1/me.must_confirm_email`). While it is set
   * the window shows the confirmation card instead of the app, and no socket
   * is opened — the gateway would refuse it. */
  mustConfirmEmail: boolean;
  selfUserId: number;
  selfUsername: string;
  directory: DirectoryState;
  rooms: Room[];
  selectedChannel: number | null;
  tab: SidebarTab;
  feed: FeedState;
  friends: FriendsState;
  presence: PresenceState;
  account: AccountState;
  calls: Record<number, Call>;
  voice: VoiceCall | null;
  ringing: Ringing | null;
  card: CardState;
  toasts: Toast[];
  /** Link cards (D280, D281): what the gateway answered, for this session. */
  linkPreviews: Record<string, LinkPreviewEntry>;
  channelCards: Record<string, ChannelCardEntry>;
  /** The composer's clock (D298): channel id → the lifetime, in seconds, that
   * whatever is sent there next goes out under; absent — it stays. Here and
   * not in the composer, because a voice note and a dropped file are sent
   * from outside the box and go under the same clock. For the session and no
   * longer: never stored, gone with the rest of the state at sign-out, and
   * nothing the gateway hears of except as `ttl_s` on a send. */
  sendTtl: Record<number, number>;
  clock: ClockState;
}

export function initialState(): UiState {
  return {
    connected: false,
    busy: false,
    notice: null,
    mustConfirmEmail: false,
    selfUserId: 0,
    selfUsername: "",
    directory: {
      people: {},
      selfId: 0,
      selfIdFallback: 0,
      lastRefreshMs: null,
      refreshWanted: false,
    },
    rooms: [],
    selectedChannel: null,
    tab: "all",
    feed: emptyFeed(),
    friends: {
      friends: [],
      incoming: [],
      outgoing: [],
      blocked: [],
      policy: "everyone",
      seeded: false,
    },
    presence: { map: {}, selfAfk: false, enabled: false },
    account: {
      displayName: null,
      bio: null,
      avatarFileId: null,
      createdAt: 0,
      bubbleLight: null,
      bubbleDark: null,
      manualStatus: null,
      isAdmin: false,
    },
    calls: {},
    voice: null,
    ringing: null,
    card: { userId: null, relation: null },
    toasts: [],
    linkPreviews: {},
    channelCards: {},
    sendTtl: {},
    clock: { samples: [], offsetMs: 0 },
  };
}
